// Project Lifecycle Recap — assembles a single, comprehensive, NON-FINANCIAL
// "total recap" of a project: every lead and every student, their current
// status + determination, WHY each is valid/invalid/pending (reconstructed from
// the client-recap trail), a chronological per-lead timeline, and project-wide
// breakdowns. Deliberately contains ZERO dollar figures, rates, pay, or margin
// so the report is safe to share with the client (confidentiality rule).
//
// Reads: projects (goals/segments), leads (project-scoped), appointments.
// Joins appointments to leads by leadId. Determination is treated as
// client-driven (the recap import is the only path to valid) — see CLAUDE.md.

import { format } from 'date-fns';
import { projectService } from '../project.service';
import { getLeads as getLeadsService } from '../lead.service';
import { appointmentService } from '../appointment.service';
import { Lead, LeadStatus, LeadDetermination } from '../../types/lead.types';
import { Appointment } from '../../types/schedule.types';

export interface DateRange {
  start: Date;
  end: Date;
}

// Shared, professional determination/status palette (also used by the PDF).
export const DETERMINATION_COLORS: Record<LeadDetermination, string> = {
  valid: '#10B981', // emerald
  invalid: '#EF4444', // red
  pending: '#F59E0B', // amber
};

export const STATUS_COLORS: Record<LeadStatus, string> = {
  uncontacted: '#6B7280',
  cold: '#3B82F6',
  warm: '#F59E0B',
  hot: '#EF4444',
  enrolled: '#10B981',
  lost: '#9CA3AF',
  dnc: '#1F2937',
};

export type TimelineKind =
  | 'captured'
  | 'determination'
  | 'recap'
  | 'appointment'
  | 'contact';

export interface LifecycleTimelineEvent {
  date: Date | null;
  dateLabel: string;
  kind: TimelineKind;
  label: string;
  detail?: string;
}

export interface LifecycleStudent {
  name: string;
  grade: string;
  campus?: string;
  status: LeadStatus;
  determination: LeadDetermination;
}

export interface LifecycleAppointment {
  date: Date | null;
  dateLabel: string;
  status: string;
  eventType?: string;
}

export interface LifecycleLead {
  id: string;
  parentName: string;
  addressLine: string; // "123 Main St, City, ST 12345"
  campus: string;
  canvasser: string;
  capturedAt: Date | null;
  capturedLabel: string;
  phone?: string;
  email?: string;
  language?: string;
  preferredContactMethod?: string;
  status: LeadStatus;
  determination: LeadDetermination;
  determinationReason: string; // the "why"
  studentCount: number;
  students: LifecycleStudent[];
  timeline: LifecycleTimelineEvent[];
  appointments: LifecycleAppointment[];
  contactAttempts: number;
  lastContactedLabel?: string;
  rideProvided?: boolean;
  attendEvent?: boolean;
  hubspotFunnel?: string;
  lat?: number;
  lng?: number;
  isDuplicate?: boolean;
  // Interest-signature capture (signature projects): signature image + the
  // collector's device GPS at signing — the per-lead accountability record.
  signature?: string; // PNG data URL
  signatureCapturedAt?: Date | null;
  signatureLat?: number;
  signatureLng?: number;
  signatureAccuracy?: number;
  consentToContact?: boolean;
  howHeard?: string;
  isBusiness?: boolean;
  businessName?: string;
  businessRole?: string;
  businessAddress?: string;
}

export interface CampusBreakdown {
  name: string;
  leads: number;
  students: number;
  valid: number; // valid leads
  enrolled: number; // enrolled leads
}

export interface CanvasserBreakdown {
  name: string;
  leads: number;
  students: number;
  validStudents: number;
}

export interface ContractProgress {
  leadGoal: number; // target valid STUDENTS (billing unit)
  leadsCurrent: number; // current valid students
  enrollmentGoal: number; // target enrolled students — hitting this satisfies the contract
  enrollmentsCurrent: number; // current enrolled students (family-inherited)
  conversionTarget: number; // required conversion %, e.g. 20
  conversionCurrent: number; // enrolled students ÷ valid students, %
  contractSatisfied: boolean; // enrollmentsCurrent >= enrollmentGoal
  conversionMet: boolean; // conversionCurrent >= conversionTarget
}

export interface LifecycleSummary {
  projectName: string;
  dateRangeLabel: string;
  generatedAtLabel: string;
  contract?: ContractProgress;
  totalLeads: number;
  totalStudents: number;
  leadsByDetermination: Record<LeadDetermination, number>;
  studentsByDetermination: Record<LeadDetermination, number>;
  leadsByStatus: Record<LeadStatus, number>;
  enrolledLeads: number;
  enrolledStudents: number;
  byCampus: CampusBreakdown[];
  byCanvasser: CanvasserBreakdown[];
  firstLeadLabel: string;
  lastLeadLabel: string;
  leadsWithAppointment: number;
  totalAppointments: number;
  goals?: {
    validLeads?: number;
    enrollmentTarget?: number;
    conversionRateTarget?: number;
  };
  dataFlags: string[];
  // 'households' = per-household counting (signature projects, via
  // project.settings.countPerHousehold): contract + headline figures count
  // leads/households instead of students. Default 'students' (Vista et al.).
  unitMode: 'students' | 'households';
  // Signature-project wording (shown per lead on the export).
  signatureAttestation?: string;
  consentText?: string;
}

export interface GeoPoint {
  lat: number;
  lng: number;
  determination: LeadDetermination;
  parentName: string;
}

export interface ProjectLifecycleReport {
  summary: LifecycleSummary;
  leads: LifecycleLead[];
  geoPoints: GeoPoint[];
}

// ---- helpers ----------------------------------------------------------------

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const statusLabel = (s: LeadStatus | string) =>
  s === 'dnc' ? 'Do Not Contact' : cap(String(s));

const toDate = (ts: any): Date | null => {
  try {
    if (!ts) return null;
    if (ts.toDate) return ts.toDate();
    const d = new Date(ts);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const dLabel = (d: Date | null) => (d ? format(d, 'MMM d, yyyy') : '—');

// Parse an appointment's ISO date-only string with the noon guard (avoids the
// UTC-shift bug documented in CLAUDE.md).
const parseApptDate = (s?: string): Date | null => {
  if (!s) return null;
  const d = new Date(`${s}T12:00:00`);
  return isNaN(d.getTime()) ? null : d;
};

const apptStatusLabel = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'scheduled':
    case 'confirmed':
      return 'Tour scheduled';
    case 'completed':
      return 'Tour completed';
    case 'cancelled':
      return 'Tour cancelled';
    case 'no-show':
    case 'no_show':
      return 'No-show';
    default:
      return `Tour ${status || 'update'}`;
  }
};

const summarizeChange = (c: { field: string; from?: string; to?: string }): string => {
  switch (c.field) {
    case 'determination':
      return `determination ${c.from || '—'} → ${cap(String(c.to))}`;
    case 'status':
      return `status → ${statusLabel(String(c.to))}`;
    case 'hubspotFunnel':
      return `funnel → "${c.to}"`;
    case 'hubspotContactType':
      return `contact type → ${c.to}`;
    case 'lastContacted':
      return `last contacted ${c.to}`;
    default:
      return `${c.field} → ${c.to}`;
  }
};

// ---- service ----------------------------------------------------------------

export const projectLifecycleService = {
  async generate(
    projectId: string,
    dateRange?: DateRange
  ): Promise<ProjectLifecycleReport> {
    const [project, allLeads, appts] = await Promise.all([
      projectService.getProject(projectId).catch(() => null as any),
      getLeadsService({ projectId }),
      appointmentService.getAppointmentsByProject(projectId).catch(() => [] as Appointment[]),
    ]);

    // Date filter (by capture date) if a range was provided.
    const leadsRaw = (allLeads || []).filter((lead) => {
      if (!dateRange) return true;
      const created = toDate(lead.createdAt);
      if (!created) return true;
      return created >= dateRange.start && created <= dateRange.end;
    });

    // Campus name resolution from project segments.
    const segments = (project?.schoolSegments || []) as Array<{ id: string; name: string }>;
    const segName = (id?: string): string | undefined => {
      if (!id) return undefined;
      return segments.find((s) => s.id === id)?.name;
    };
    const leadCampus = (lead: Lead): string =>
      lead.schoolSegmentName || segName(lead.schoolSegmentId) || 'Unassigned';

    // Appointments grouped by leadId.
    const apptsByLead = new Map<string, Appointment[]>();
    (appts || []).forEach((a) => {
      if (!a.leadId) return;
      const arr = apptsByLead.get(a.leadId) || [];
      arr.push(a);
      apptsByLead.set(a.leadId, arr);
    });

    // Duplicate detection (by normalized parent name).
    const nameCounts = new Map<string, number>();
    leadsRaw.forEach((l) => {
      const k = (l.parentName || '').trim().toLowerCase();
      if (k) nameCounts.set(k, (nameCounts.get(k) || 0) + 1);
    });

    const leads: LifecycleLead[] = leadsRaw.map((lead) => {
      const det = (lead.determination || 'pending') as LeadDetermination;
      const campus = leadCampus(lead);
      const capturedAt = toDate(lead.createdAt);

      const students: LifecycleStudent[] = (lead.children || []).map((c) => ({
        name: c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Student',
        grade: c.grade || '—',
        campus: segName(c.schoolSegmentId) || campus,
        status: (c.status || lead.status) as LeadStatus,
        determination: (c.determination || det) as LeadDetermination,
      }));

      // ---- WHY valid/invalid/pending ----
      const funnel = lead.hubspotFunnel;
      const uh = lead.updateHistory || [];
      const recapDetChange = [...uh]
        .reverse()
        .find((h) => (h.changes || []).some((c) => c.field === 'determination' && c.to === det));
      let determinationReason: string;
      if (det === 'valid') {
        if (recapDetChange) {
          determinationReason = `Confirmed valid on the ${dLabel(
            toDate(recapDetChange.timestamp)
          )} client recap${funnel ? ` (client funnel: "${funnel}")` : ''}.`;
        } else {
          const dh = [...(lead.determinationHistory || [])]
            .reverse()
            .find((e) => e.determination === 'valid');
          determinationReason = dh
            ? `Marked valid${dh.userName && dh.userName !== 'System' ? ` by ${dh.userName}` : ''}${
                toDate(dh.timestamp) ? ` on ${dLabel(toDate(dh.timestamp))}` : ''
              }${dh.notes ? ` — ${dh.notes}` : ''}.`
            : 'Marked valid (source not recorded).';
        }
      } else if (det === 'invalid') {
        if (recapDetChange) {
          determinationReason = `Marked invalid on the ${dLabel(
            toDate(recapDetChange.timestamp)
          )} client recap${funnel ? ` (client funnel: "${funnel}")` : ''}.`;
        } else {
          const dh = [...(lead.determinationHistory || [])]
            .reverse()
            .find((e) => e.determination === 'invalid');
          determinationReason = dh
            ? `Marked invalid${dh.userName && dh.userName !== 'System' ? ` by ${dh.userName}` : ''}${
                dh.notes ? ` — ${dh.notes}` : ''
              }.`
            : 'Marked invalid (source not recorded).';
        }
      } else {
        determinationReason = `Not yet returned as valid on any client recap${
          funnel ? `; currently at funnel stage "${funnel}"` : ' (awaiting the client’s next recap)'
        }.`;
      }

      // ---- timeline ----
      const events: LifecycleTimelineEvent[] = [];
      if (capturedAt) {
        events.push({
          date: capturedAt,
          dateLabel: dLabel(capturedAt),
          kind: 'captured',
          label: `Captured by ${lead.capturedByName || 'field staff'}`,
          detail: lead.source ? `source: ${lead.source}` : undefined,
        });
      }
      // Determination values that recaps already account for — used to suppress
      // duplicate determinationHistory noise (the importer re-stamps history).
      const recapDetValues = new Set<string>();
      uh.forEach((h) =>
        (h.changes || []).forEach((c) => {
          if (c.field === 'determination' && c.to) recapDetValues.add(c.to);
        })
      );
      (lead.determinationHistory || []).forEach((e) => {
        if (recapDetValues.has(e.determination)) return; // covered by a recap entry
        const d = toDate(e.timestamp);
        events.push({
          date: d,
          dateLabel: dLabel(d),
          kind: 'determination',
          label:
            e.userName === 'System'
              ? `Auto-set to ${cap(e.determination)} (system)`
              : `Determination set to ${cap(e.determination)}`,
          detail: e.userName && e.userName !== 'System' ? `by ${e.userName}` : undefined,
        });
      });
      uh.forEach((h) => {
        const d = toDate(h.timestamp);
        const parts = (h.changes || []).map(summarizeChange);
        events.push({
          date: d,
          dateLabel: dLabel(d),
          kind: 'recap',
          label: h.source || 'Client recap update',
          detail: parts.join('; ') || undefined,
        });
      });
      const leadAppts = (apptsByLead.get(lead.id) || [])
        .map((a) => ({
          date: parseApptDate(a.date),
          dateLabel: dLabel(parseApptDate(a.date)),
          status: a.status,
          eventType: a.eventType,
        }))
        .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
      leadAppts.forEach((a) => {
        events.push({
          date: a.date,
          dateLabel: a.dateLabel,
          kind: 'appointment',
          label: apptStatusLabel(a.status),
          detail: a.eventType ? `${a.eventType}` : undefined,
        });
      });
      events.sort((a, b) => {
        const ta = a.date ? a.date.getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.date ? b.date.getTime() : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });

      const lastContacted = toDate(lead.lastContacted);
      const addressLine = [
        [lead.address, lead.unit ? `#${lead.unit}` : ''].filter(Boolean).join(' '),
        lead.city,
        [lead.state, lead.zipCode].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ');

      return {
        id: lead.id,
        parentName: lead.parentName || 'Unknown',
        addressLine: addressLine || '—',
        campus,
        canvasser: lead.capturedByName || 'Unassigned',
        capturedAt,
        capturedLabel: dLabel(capturedAt),
        phone: lead.phone,
        email: lead.email,
        language: lead.language === 'es' ? 'Español' : lead.language === 'en' ? 'English' : lead.language,
        preferredContactMethod: lead.preferredContactMethod,
        status: (lead.status || 'uncontacted') as LeadStatus,
        determination: det,
        determinationReason,
        studentCount: students.length,
        students,
        timeline: events,
        appointments: leadAppts,
        contactAttempts: (lead.contactAttempts || []).length,
        lastContactedLabel: lastContacted ? dLabel(lastContacted) : undefined,
        rideProvided: lead.rideProvided,
        attendEvent: lead.attendEvent,
        hubspotFunnel: lead.hubspotFunnel,
        lat: lead.gpsLocation?.latitude,
        lng: lead.gpsLocation?.longitude,
        isDuplicate: (nameCounts.get((lead.parentName || '').trim().toLowerCase()) || 0) > 1,
        signature: lead.signature,
        signatureCapturedAt: (lead.signatureCapturedAt as any)?.toDate?.() || null,
        signatureLat: lead.signatureLocation?.latitude,
        signatureLng: lead.signatureLocation?.longitude,
        signatureAccuracy: lead.signatureLocation?.accuracy,
        consentToContact: lead.consentToContact,
        howHeard: lead.howHeard,
        isBusiness: lead.isBusiness,
        businessName: lead.businessName,
        businessRole: lead.businessRole,
        businessAddress: lead.businessAddress,
      };
    });

    // ---- summary aggregates ----
    const leadsByDetermination: Record<LeadDetermination, number> = { valid: 0, invalid: 0, pending: 0 };
    const studentsByDetermination: Record<LeadDetermination, number> = { valid: 0, invalid: 0, pending: 0 };
    const leadsByStatus: Record<LeadStatus, number> = {
      uncontacted: 0, cold: 0, warm: 0, hot: 0, enrolled: 0, lost: 0, dnc: 0,
    };
    let totalStudents = 0;
    let enrolledLeads = 0;
    let enrolledStudents = 0;
    let leadsWithAppointment = 0;
    const campusMap = new Map<string, CampusBreakdown>();
    const canvasserMap = new Map<string, CanvasserBreakdown>();
    let firstLead: Date | null = null;
    let lastLead: Date | null = null;

    leads.forEach((l) => {
      leadsByDetermination[l.determination]++;
      leadsByStatus[l.status] = (leadsByStatus[l.status] || 0) + 1;
      totalStudents += l.studentCount;
      if (l.status === 'enrolled') enrolledLeads++;
      if (l.appointments.length) leadsWithAppointment++;
      // Enrollment is counted PER STUDENT. On recap import each child in a newly
      // enrolled family is defaulted to enrolled, and a child known not to have
      // enrolled is flipped back in Lead Details — so this per-student filter
      // reflects partial-family enrollment accurately.
      l.students.forEach((s) => {
        studentsByDetermination[s.determination]++;
        if (s.status === 'enrolled') enrolledStudents++;
      });
      if (l.capturedAt) {
        if (!firstLead || l.capturedAt < firstLead) firstLead = l.capturedAt;
        if (!lastLead || l.capturedAt > lastLead) lastLead = l.capturedAt;
      }
      // campus
      const cb = campusMap.get(l.campus) || { name: l.campus, leads: 0, students: 0, valid: 0, enrolled: 0 };
      cb.leads++;
      cb.students += l.studentCount;
      if (l.determination === 'valid') cb.valid++;
      if (l.status === 'enrolled') cb.enrolled++;
      campusMap.set(l.campus, cb);
      // canvasser
      const cv = canvasserMap.get(l.canvasser) || { name: l.canvasser, leads: 0, students: 0, validStudents: 0 };
      cv.leads++;
      cv.students += l.studentCount;
      cv.validStudents += l.students.filter((s) => s.determination === 'valid').length;
      canvasserMap.set(l.canvasser, cv);
    });

    // Data-quality flags.
    const dataFlags: string[] = [];
    const dupeNames = Array.from(nameCounts.entries()).filter(([, n]) => n > 1);
    if (dupeNames.length) {
      dataFlags.push(
        `${dupeNames.length} duplicate parent name${dupeNames.length > 1 ? 's' : ''} (${dupeNames
          .map(([n, c]) => `${n.replace(/\b\w/g, (m) => m.toUpperCase())} ×${c}`)
          .slice(0, 6)
          .join(', ')}) — may inflate lead counts.`
      );
    }
    if ((project?.settings as any)?.countPerHousehold !== true && enrolledLeads > 0 && enrolledStudents < enrolledLeads) {
      dataFlags.push(
        `${enrolledLeads} lead${enrolledLeads > 1 ? 's are' : ' is'} marked Enrolled, but only ${enrolledStudents} student${
          enrolledStudents === 1 ? '' : 's'
        } carry an Enrolled status — student-level enrollment is under-recorded.`
      );
    }

    // Contract progress is measured in STUDENTS — billing is per valid student
    // and each child is its own billable lead. Valid students vs goal; enrolled
    // students vs goal (the contract-satisfying milestone); conversion =
    // enrolled ÷ valid students.
    const validStudentCount = studentsByDetermination.valid;
    // Per-household mode (signature projects): the contract unit is signed
    // households, so all contract math runs on lead counts instead of students.
    const perHousehold = (project?.settings as any)?.countPerHousehold === true;
    const validUnit = perHousehold ? leadsByDetermination.valid : validStudentCount;
    const enrolledUnit = perHousehold ? enrolledLeads : enrolledStudents;
    const conversionCurrent = validUnit > 0 ? (enrolledUnit / validUnit) * 100 : 0;
    const enrollGoal = project?.goals?.enrollmentTarget || 0;
    const convTarget = (project?.goals as any)?.conversionRateTarget || 0;
    const contract: ContractProgress | undefined = project?.goals
      ? {
          leadGoal: project.goals.validLeads || 0,
          leadsCurrent: validUnit,
          enrollmentGoal: enrollGoal,
          enrollmentsCurrent: enrolledUnit,
          conversionTarget: convTarget,
          conversionCurrent: Math.round(conversionCurrent * 10) / 10,
          contractSatisfied: enrollGoal > 0 && enrolledUnit >= enrollGoal,
          conversionMet: conversionCurrent >= convTarget,
        }
      : undefined;

    const summary: LifecycleSummary = {
      projectName: project?.name || 'Project',
      dateRangeLabel: dateRange
        ? `${format(dateRange.start, 'MMM d, yyyy')} – ${format(dateRange.end, 'MMM d, yyyy')}`
        : 'All time',
      generatedAtLabel: format(new Date(), 'MMMM d, yyyy'),
      contract,
      totalLeads: leads.length,
      totalStudents,
      leadsByDetermination,
      studentsByDetermination,
      leadsByStatus,
      enrolledLeads,
      enrolledStudents,
      byCampus: Array.from(campusMap.values()).sort((a, b) => b.leads - a.leads),
      byCanvasser: Array.from(canvasserMap.values()).sort((a, b) => b.validStudents - a.validStudents),
      firstLeadLabel: dLabel(firstLead),
      lastLeadLabel: dLabel(lastLead),
      leadsWithAppointment,
      totalAppointments: (appts || []).length,
      goals: project?.goals
        ? {
            validLeads: project.goals.validLeads,
            enrollmentTarget: project.goals.enrollmentTarget,
            conversionRateTarget: (project.goals as any).conversionRateTarget,
          }
        : undefined,
      dataFlags,
      unitMode: perHousehold ? 'households' : 'students',
      signatureAttestation: (project?.settings as any)?.signatureAttestation,
      consentText: (project?.settings as any)?.consentText,
    };

    // Sort leads: valid → invalid → pending, then campus, then name.
    const detOrder: Record<LeadDetermination, number> = { valid: 0, invalid: 1, pending: 2 };
    leads.sort((a, b) => {
      if (detOrder[a.determination] !== detOrder[b.determination])
        return detOrder[a.determination] - detOrder[b.determination];
      if (a.campus !== b.campus) return a.campus.localeCompare(b.campus);
      return a.parentName.localeCompare(b.parentName);
    });

    const geoPoints: GeoPoint[] = leads
      .filter((l) => typeof l.lat === 'number' && typeof l.lng === 'number')
      .map((l) => ({ lat: l.lat as number, lng: l.lng as number, determination: l.determination, parentName: l.parentName }));

    return { summary, leads, geoPoints };
  },
};
